import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChartLineUp, ArrowsClockwise, FilePdf } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SemaforoChip } from "./faUtils";
import FaResumen from "./FaResumen";
import FaBudgetReal from "./FaBudgetReal";
import FaCapture from "./FaCapture";
import FaSensitivity from "./FaSensitivity";
import FaParams from "./FaParams";

const THIS_YEAR = new Date().getFullYear();
const YEARS = [THIS_YEAR - 1, THIS_YEAR, THIS_YEAR + 1];
const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
                    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

export default function FinancialAnalysis({ embedded = false }) {
  const { t } = useLang();
  const { has } = usePermissions();
  const canWrite = has("finanalysis.write") || (embedded && has("finance.write"));
  const [year, setYear] = useState(THIS_YEAR);
  // Iter117: selector de mes GLOBAL — se propaga a Budget vs Real y Captura
  // Mensual como prop controlada, para que el usuario navegue entre meses
  // sin cambiar de tab.
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/finanalysis/report/${year}`);
      setReport(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const downloadExecutiveSummary = async () => {
    try {
      const r = await api.get(`/finanalysis/executive-summary/${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `resumen_ejecutivo_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Resumen Ejecutivo descargado");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="finanalysis-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className={embedded ? "hidden" : ""}>
          <h1 className="font-heading font-black text-2xl text-slate-900 flex items-center gap-2.5">
            <ChartLineUp size={26} weight="fill" className="text-blue-900" />
            {t("finAnalysisTitle")}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {t("finAnalysisSubtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          {report && <SemaforoChip value={report.semaforo_anual} testid="fa-semaforo-anual" />}
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-36" data-testid="fa-month-global-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {MONTHS_ES.map((l, i) => (
                <SelectItem key={i} value={String(i + 1)}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="fa-year-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="fa-refresh-btn">
            <ArrowsClockwise size={14} weight="bold" /> Actualizar
          </Button>
          <Button
            size="sm"
            onClick={downloadExecutiveSummary}
            disabled={!report}
            className="gap-1.5 bg-blue-900 hover:bg-blue-800"
            data-testid="fa-exec-summary-btn"
          >
            <FilePdf size={14} weight="bold" /> Resumen Ejecutivo PDF
          </Button>
        </div>
      </div>

      {loading && !report ? (
        <div className="py-24 text-center text-slate-400 text-sm">Calculando reporte financiero…</div>
      ) : report && (
        <Tabs defaultValue="resumen">
          <TabsList className="bg-slate-100 flex-wrap h-auto">
            <TabsTrigger value="resumen" data-testid="fa-tab-resumen">Resumen Ejecutivo</TabsTrigger>
            <TabsTrigger value="budget" data-testid="fa-tab-budget">Budget vs Real</TabsTrigger>
            {canWrite && <TabsTrigger value="captura" data-testid="fa-tab-captura">Captura Mensual</TabsTrigger>}
            <TabsTrigger value="sensibilidad" data-testid="fa-tab-sensibilidad">Sensibilidad</TabsTrigger>
            <TabsTrigger value="parametros" data-testid="fa-tab-parametros">Parámetros</TabsTrigger>
          </TabsList>
          <TabsContent value="resumen"><FaResumen report={report} /></TabsContent>
          <TabsContent value="budget"><FaBudgetReal report={report} month={month} onMonthChange={setMonth} /></TabsContent>
          {canWrite && (
            <TabsContent value="captura">
              <FaCapture year={year} month={month} onMonthChange={setMonth} report={report} onSaved={load} />
            </TabsContent>
          )}
          <TabsContent value="sensibilidad"><FaSensitivity year={year} /></TabsContent>
          <TabsContent value="parametros">
            <FaParams year={year} report={report} canWrite={canWrite} onChanged={load} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
